import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Plus, Edit2, Trash2, Key, X, Check, PhoneCall, Users } from 'lucide-react';
import { useSortable, SortTh } from '../hooks/useSortable';
import {
  listCallQueues, syncCallQueues, createCallQueue, updateCallQueue, deleteCallQueue,
  listResourceAccounts, createResourceAccount, updateResourceAccount, deleteResourceAccount,
  assignLicense, pollJob, getQueueMembers, updateQueueMembers, searchEntra,
} from '../services/portalApi';

function flashMsg(setFlash, type, text) {
  setFlash({ type, text });
  setTimeout(() => setFlash(null), 4000);
}

function LicenseBadge({ status }) {
  if (status === 'licensed') return (
    <span className="badge badge-green"><span className="dot green" />Licensed</span>
  );
  if (status === 'unlicensed') return (
    <span className="badge badge-red"><span className="dot red" />Unlicensed</span>
  );
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
      background: 'var(--border)', color: 'var(--muted)', border: '1px solid var(--border-strong)',
    }}>Unknown</span>
  );
}

const ROUTING_METHODS = ['Attendant', 'Serial', 'RoundRobin', 'LongestIdle'];
const OVERFLOW_ACTIONS = ['DisconnectWithBusy', 'Forward', 'Voicemail'];
const TIMEOUT_ACTIONS = ['Disconnect', 'Forward', 'Voicemail'];

function SearchDropdown({ placeholder, value, onChange, results, searching, onSelect }) {
  const show = value.length >= 2 && (results.length > 0 || searching);
  return (
    <div style={{ position: 'relative' }}>
      <input className="input" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
      {show && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60,
          background: '#0f1524', border: '1px solid var(--border)', borderRadius: 6,
          boxShadow: '0 8px 24px rgba(0,0,0,.6)', maxHeight: 200, overflowY: 'auto',
        }}>
          {searching && <div style={{ padding: '8px 12px', color: 'var(--muted-3)', fontSize: 12 }}>Searching…</div>}
          {results.map(r => (
            <div
              key={r.id}
              onClick={() => onSelect(r)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{r.displayName}</div>
              {r.sub && <div style={{ fontSize: 11, color: 'var(--muted-3)' }}>{r.sub}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MemberList({ items, onRemove, emptyText }) {
  if (!items.length) return (
    <div style={{ fontSize: 12, color: 'var(--muted-3)', textAlign: 'center', padding: '6px 0' }}>{emptyText}</div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflowY: 'auto' }}>
      {items.map(item => (
        <div key={String(item.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: '#1a1f35', borderRadius: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(item.displayName ?? item.id ?? '')}</div>
            {item.sub && <div style={{ fontSize: 11, color: 'var(--muted-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(item.sub)}</div>}
          </div>
          <button
            onClick={() => onRemove(item.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-3)', padding: 2, flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--err)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted-3)'}
          >
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

function MembersModal({ queue, onClose, onSave, effectiveCid }) {
  const [routingMethod, setRoutingMethod] = useState(queue.routing_method || 'Attendant');
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const [userQuery, setUserQuery]     = useState('');
  const [groupQuery, setGroupQuery]   = useState('');
  const [userResults, setUserResults] = useState([]);
  const [groupResults, setGroupResults] = useState([]);
  const [searchingU, setSearchingU]   = useState(false);
  const [searchingG, setSearchingG]   = useState(false);

  const customerId = queue.customer_id || effectiveCid;

  useEffect(() => {
    getQueueMembers(queue.id).then(data => {
      setAgents(data.agents || []);
      setGroups(data.groups || []);
      setLoading(false);
    }).catch(e => { setErr(e.message); setLoading(false); });
  }, [queue.id]);

  useEffect(() => {
    if (userQuery.length < 2) { setUserResults([]); return; }
    const t = setTimeout(() => {
      setSearchingU(true);
      searchEntra(userQuery, 'users', customerId)
        .then(d => setUserResults(d.results || []))
        .catch(() => {})
        .finally(() => setSearchingU(false));
    }, 300);
    return () => clearTimeout(t);
  }, [userQuery, customerId]);

  useEffect(() => {
    if (groupQuery.length < 2) { setGroupResults([]); return; }
    const t = setTimeout(() => {
      setSearchingG(true);
      searchEntra(groupQuery, 'groups', customerId)
        .then(d => setGroupResults(d.results || []))
        .catch(() => {})
        .finally(() => setSearchingG(false));
    }, 300);
    return () => clearTimeout(t);
  }, [groupQuery, customerId]);

  function addAgent(item) {
    if (agents.some(a => a.id === item.id)) return;
    setAgents(prev => [...prev, item]);
    setUserQuery(''); setUserResults([]);
  }
  function addGroup(item) {
    if (groups.some(g => g.id === item.id)) return;
    setGroups(prev => [...prev, item]);
    setGroupQuery(''); setGroupResults([]);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(queue.id, {
        routing_method: routingMethod,
        agents: agents.map(a => a.id),
        groups: groups.map(g => g.id),
      });
      onClose();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <span className="modal-title">Configure Members — {queue.name}</span>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label className="field-label">Routing Method</label>
            <select className="input" value={routingMethod} onChange={e => setRoutingMethod(e.target.value)}>
              {ROUTING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ color: 'var(--muted-3)', textAlign: 'center', padding: 20 }}>Loading members…</div>
          ) : (
            <>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                  Agents ({agents.length})
                </div>
                <div style={{ marginBottom: 8 }}>
                  <SearchDropdown
                    placeholder="Search users to add…"
                    value={userQuery}
                    onChange={setUserQuery}
                    results={userResults}
                    searching={searchingU}
                    onSelect={addAgent}
                  />
                </div>
                <MemberList items={agents} onRemove={id => setAgents(prev => prev.filter(a => a.id !== id))} emptyText="No agents — search above to add" />
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                  Distribution Groups ({groups.length})
                </div>
                <div style={{ marginBottom: 8 }}>
                  <SearchDropdown
                    placeholder="Search Entra groups to add…"
                    value={groupQuery}
                    onChange={setGroupQuery}
                    results={groupResults}
                    searching={searchingG}
                    onSelect={addGroup}
                  />
                </div>
                <MemberList items={groups} onRemove={id => setGroups(prev => prev.filter(g => g.id !== id))} emptyText="No groups — search above to add" />
              </div>
            </>
          )}

          {err && <div style={{ color: 'var(--err)', fontSize: 12 }}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddQueueModal({ onClose, onSave, customerId, customers, isAdmin }) {
  const [form, setForm] = useState({
    name: '',
    routing_method: 'Attendant',
    overflow_threshold: 50,
    overflow_action: 'DisconnectWithBusy',
    overflow_target: '',
    timeout_threshold: 30,
    timeout_action: 'Disconnect',
    timeout_target: '',
    music_on_hold: 1,
    customerId: customerId || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">Add Call Queue</span>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {isAdmin && customers.length > 0 && (
              <div>
                <label className="field-label">Customer</label>
                <select className="input" value={form.customerId} onChange={e => set('customerId', e.target.value)}>
                  <option value="">— select customer —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="field-label">Name</label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Main Support Queue" autoFocus />
            </div>
            <div>
              <label className="field-label">Routing Method</label>
              <select className="input" value={form.routing_method} onChange={e => set('routing_method', e.target.value)}>
                {ROUTING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="field-label">Overflow After (calls)</label>
                <input className="input" type="number" min={0} value={form.overflow_threshold} onChange={e => set('overflow_threshold', Number(e.target.value))} />
              </div>
              <div>
                <label className="field-label">Overflow Action</label>
                <select className="input" value={form.overflow_action} onChange={e => set('overflow_action', e.target.value)}>
                  {OVERFLOW_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            {form.overflow_action === 'Forward' && (
              <div>
                <label className="field-label">Overflow Target</label>
                <input className="input" value={form.overflow_target} onChange={e => set('overflow_target', e.target.value)} placeholder="SIP URI or phone number" />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="field-label">Timeout After (seconds)</label>
                <input className="input" type="number" min={0} value={form.timeout_threshold} onChange={e => set('timeout_threshold', Number(e.target.value))} />
              </div>
              <div>
                <label className="field-label">Timeout Action</label>
                <select className="input" value={form.timeout_action} onChange={e => set('timeout_action', e.target.value)}>
                  {TIMEOUT_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            {form.timeout_action === 'Forward' && (
              <div>
                <label className="field-label">Timeout Target</label>
                <input className="input" value={form.timeout_target} onChange={e => set('timeout_target', e.target.value)} placeholder="SIP URI or phone number" />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label className="field-label" style={{ margin: 0 }}>Music on Hold</label>
              <button
                type="button"
                onClick={() => set('music_on_hold', form.music_on_hold ? 0 : 1)}
                style={{
                  width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: form.music_on_hold ? 'var(--info)' : 'var(--muted-3)',
                  position: 'relative', transition: 'background .2s',
                }}
              >
                <span style={{
                  position: 'absolute', top: 2, left: form.music_on_hold ? 18 : 2,
                  width: 16, height: 16, borderRadius: '50%', background: 'var(--on-brand)',
                  transition: 'left .2s',
                }} />
              </button>
              <span style={{ fontSize: 12, color: form.music_on_hold ? 'var(--info)' : 'var(--muted-3)' }}>
                {form.music_on_hold ? 'On' : 'Off'}
              </span>
            </div>
            {err && <div style={{ color: 'var(--err)', fontSize: 12 }}>{err}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create Queue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditQueueModal({ queue, onClose, onSave }) {
  const [form, setForm] = useState({
    name: queue.name,
    routing_method: queue.routing_method,
    overflow_threshold: queue.overflow_threshold,
    overflow_action: queue.overflow_action,
    overflow_target: queue.overflow_target || '',
    timeout_threshold: queue.timeout_threshold,
    timeout_action: queue.timeout_action,
    timeout_target: queue.timeout_target || '',
    music_on_hold: queue.music_on_hold,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(queue.id, form);
      onClose();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">Edit Call Queue</span>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="field-label">Name</label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
            </div>
            <div>
              <label className="field-label">Routing Method</label>
              <select className="input" value={form.routing_method} onChange={e => set('routing_method', e.target.value)}>
                {ROUTING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="field-label">Overflow After (calls)</label>
                <input className="input" type="number" min={0} value={form.overflow_threshold} onChange={e => set('overflow_threshold', Number(e.target.value))} />
              </div>
              <div>
                <label className="field-label">Overflow Action</label>
                <select className="input" value={form.overflow_action} onChange={e => set('overflow_action', e.target.value)}>
                  {OVERFLOW_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            {form.overflow_action === 'Forward' && (
              <div>
                <label className="field-label">Overflow Target</label>
                <input className="input" value={form.overflow_target} onChange={e => set('overflow_target', e.target.value)} placeholder="SIP URI or phone number" />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="field-label">Timeout After (seconds)</label>
                <input className="input" type="number" min={0} value={form.timeout_threshold} onChange={e => set('timeout_threshold', Number(e.target.value))} />
              </div>
              <div>
                <label className="field-label">Timeout Action</label>
                <select className="input" value={form.timeout_action} onChange={e => set('timeout_action', e.target.value)}>
                  {TIMEOUT_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
            {form.timeout_action === 'Forward' && (
              <div>
                <label className="field-label">Timeout Target</label>
                <input className="input" value={form.timeout_target} onChange={e => set('timeout_target', e.target.value)} placeholder="SIP URI or phone number" />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label className="field-label" style={{ margin: 0 }}>Music on Hold</label>
              <button
                type="button"
                onClick={() => set('music_on_hold', form.music_on_hold ? 0 : 1)}
                style={{
                  width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: form.music_on_hold ? 'var(--info)' : 'var(--muted-3)',
                  position: 'relative', transition: 'background .2s',
                }}
              >
                <span style={{
                  position: 'absolute', top: 2, left: form.music_on_hold ? 18 : 2,
                  width: 16, height: 16, borderRadius: '50%', background: 'var(--on-brand)',
                  transition: 'left .2s',
                }} />
              </button>
              <span style={{ fontSize: 12, color: form.music_on_hold ? 'var(--info)' : 'var(--muted-3)' }}>
                {form.music_on_hold ? 'On' : 'Off'}
              </span>
            </div>
            {err && <div style={{ color: 'var(--err)', fontSize: 12 }}>{err}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddResourceAccountModal({ onClose, onSave, customerId, customers, isAdmin, callQueues }) {
  const [form, setForm] = useState({
    display_name: '',
    upn: '',
    application_type: 'CallQueue',
    queue_id: '',
    customerId: customerId || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.display_name.trim() || !form.upn.trim()) { setErr('Display name and UPN are required'); return; }
    setSaving(true);
    try {
      await onSave({ ...form, queue_id: form.queue_id || null });
      onClose();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <span className="modal-title">Add Resource Account</span>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {isAdmin && customers.length > 0 && (
              <div>
                <label className="field-label">Customer</label>
                <select className="input" value={form.customerId} onChange={e => set('customerId', e.target.value)}>
                  <option value="">— select customer —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="field-label">Display Name</label>
              <input className="input" value={form.display_name} onChange={e => set('display_name', e.target.value)} placeholder="Main Queue RA" autoFocus />
            </div>
            <div>
              <label className="field-label">UPN</label>
              <input className="input" value={form.upn} onChange={e => set('upn', e.target.value)} placeholder="cq-mainqueue@tenant.onmicrosoft.com" />
            </div>
            <div>
              <label className="field-label">Type</label>
              <select className="input" value={form.application_type} onChange={e => set('application_type', e.target.value)}>
                <option value="CallQueue">Call Queue</option>
                <option value="AutoAttendant">Auto Attendant</option>
              </select>
            </div>
            <div>
              <label className="field-label">Link to Queue (optional)</label>
              <select className="input" value={form.queue_id} onChange={e => set('queue_id', e.target.value)}>
                <option value="">— none —</option>
                {callQueues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </div>
            {err && <div style={{ color: 'var(--err)', fontSize: 12 }}>{err}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditResourceAccountModal({ ra, onClose, onSave, callQueues }) {
  const [form, setForm] = useState({
    display_name: ra.display_name,
    queue_id: ra.queue_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(ra.id, { ...form, queue_id: form.queue_id || null });
      onClose();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <span className="modal-title">Edit Resource Account</span>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="field-label">Display Name</label>
              <input className="input" value={form.display_name} onChange={e => set('display_name', e.target.value)} autoFocus />
            </div>
            <div>
              <label className="field-label">Link to Queue (optional)</label>
              <select className="input" value={form.queue_id} onChange={e => set('queue_id', e.target.value)}>
                <option value="">— none —</option>
                {callQueues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </div>
            {err && <div style={{ color: 'var(--err)', fontSize: 12 }}>{err}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const str = iso.includes('T') ? iso : iso.replace(' ', 'T');
    const d = new Date(str.endsWith('Z') || str.includes('+') ? str : str + 'Z');
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function CallQueues({ user, viewAsCustomer, customers = [] }) {
  const [queues,        setQueues]        = useState([]);
  const [accounts,      setAccounts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [syncing,       setSyncing]       = useState(false);
  const [flash,         setFlash]         = useState(null);
  const [addQueueOpen,  setAddQueueOpen]  = useState(false);
  const [editQueue,     setEditQueue]     = useState(null);
  const [membersQueue,  setMembersQueue]  = useState(null);
  const [addRaOpen,     setAddRaOpen]     = useState(false);
  const [editRa,        setEditRa]        = useState(null);
  const [licensingRaId, setLicensingRaId] = useState(null);

  const effectiveCid = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.customerId);
  const isAdmin = user?.isAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cqData, raData] = await Promise.all([
        listCallQueues(effectiveCid),
        listResourceAccounts(effectiveCid),
      ]);
      setQueues(cqData.callQueues || []);
      setAccounts(raData.resourceAccounts || []);
    } catch (e) {
      flashMsg(setFlash, 'error', e.message);
    } finally {
      setLoading(false);
    }
  }, [effectiveCid]);

  useEffect(() => { load(); }, [load]);

  async function handleSync() {
    setSyncing(true);
    try {
      const { jobId } = await syncCallQueues(effectiveCid);
      flashMsg(setFlash, 'success', 'Sync started…');
      pollJob(jobId, job => {
        if (job.status === 'done') {
          flashMsg(setFlash, 'success', job.message || 'Sync complete');
          load();
        } else {
          flashMsg(setFlash, 'error', job.message || 'Sync failed');
        }
        setSyncing(false);
      });
    } catch (e) {
      flashMsg(setFlash, 'error', e.message);
      setSyncing(false);
    }
  }

  async function handleCreateQueue(form) {
    const { jobId } = await createCallQueue(form);
    flashMsg(setFlash, 'success', 'Creating call queue…');
    pollJob(jobId, job => {
      flashMsg(setFlash, job.status === 'done' ? 'success' : 'error', job.message || (job.status === 'done' ? 'Done' : 'Failed'));
      load();
    });
  }

  async function handleUpdateQueue(id, form) {
    const { jobId } = await updateCallQueue(id, form);
    flashMsg(setFlash, 'success', 'Updating call queue…');
    pollJob(jobId, job => {
      flashMsg(setFlash, job.status === 'done' ? 'success' : 'error', job.message || (job.status === 'done' ? 'Done' : 'Failed'));
      load();
    });
  }

  async function handleUpdateMembers(id, data) {
    const { jobId } = await updateQueueMembers(id, data);
    flashMsg(setFlash, 'success', 'Updating members…');
    pollJob(jobId, job => {
      flashMsg(setFlash, job.status === 'done' ? 'success' : 'error', job.message || (job.status === 'done' ? 'Done' : 'Failed'));
      load();
    });
  }

  async function handleDeleteQueue(q) {
    if (!window.confirm(`Delete call queue "${q.name}"?`)) return;
    try {
      await deleteCallQueue(q.id);
      flashMsg(setFlash, 'success', `Deleted "${q.name}"`);
      load();
    } catch (e) {
      flashMsg(setFlash, 'error', e.message);
    }
  }

  async function handleCreateRa(form) {
    const { jobId } = await createResourceAccount(form);
    flashMsg(setFlash, 'success', 'Creating resource account…');
    pollJob(jobId, job => {
      flashMsg(setFlash, job.status === 'done' ? 'success' : 'error', job.message || (job.status === 'done' ? 'Done' : 'Failed'));
      load();
    });
  }

  async function handleUpdateRa(id, form) {
    const { jobId } = await updateResourceAccount(id, form);
    flashMsg(setFlash, 'success', 'Updating resource account…');
    pollJob(jobId, job => {
      flashMsg(setFlash, job.status === 'done' ? 'success' : 'error', job.message || (job.status === 'done' ? 'Done' : 'Failed'));
      load();
    });
  }

  async function handleDeleteRa(ra) {
    if (!window.confirm(`Delete resource account "${ra.display_name}"?`)) return;
    try {
      await deleteResourceAccount(ra.id);
      flashMsg(setFlash, 'success', `Deleted "${ra.display_name}"`);
      load();
    } catch (e) {
      flashMsg(setFlash, 'error', e.message);
    }
  }

  async function handleLicense(ra, action) {
    if (action === 'remove') {
      if (!window.confirm(`Remove the Teams Phone Resource Account license from "${ra.display_name}"? The resource account will become unlicensed.`)) return;
    }
    setLicensingRaId(ra.id);
    try {
      const result = await assignLicense(ra.id, action);
      flashMsg(setFlash, 'success', `License ${result.status} for ${ra.display_name}`);
      load();
    } catch (e) {
      flashMsg(setFlash, 'error', e.message);
    } finally {
      setLicensingRaId(null);
    }
  }

  const queueSortAccessors = useMemo(() => ({
    agents: q => (Array.isArray(q.agents_json) ? q.agents_json : JSON.parse(q.agents_json || '[]')).length +
                 (Array.isArray(q.groups_json) ? q.groups_json : JSON.parse(q.groups_json || '[]')).length,
  }), []);
  const { sortKey: qSortKey, sortDir: qSortDir, handleSort: qHandleSort, sortedRows: sortedQueues } = useSortable(queues, queueSortAccessors);

  const raSortAccessors = useMemo(() => ({
    queue: ra => ra.linked_queue_name || '',
  }), []);
  const { sortKey: raSortKey, sortDir: raSortDir, handleSort: raHandleSort, sortedRows: sortedAccounts } = useSortable(accounts, raSortAccessors);

  return (
    <div className="page">
      {flash && (
        <div className={`flash flash-${flash.type}`} style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999 }}>
          {flash.type === 'success' ? <Check size={14} /> : <X size={14} />}
          {flash.text}
        </div>
      )}

      {addQueueOpen && (
        <AddQueueModal
          onClose={() => setAddQueueOpen(false)}
          onSave={handleCreateQueue}
          customerId={effectiveCid}
          customers={customers.filter(c => !c.isAdmin)}
          isAdmin={isAdmin}
        />
      )}

      {editQueue && (
        <EditQueueModal
          queue={editQueue}
          onClose={() => setEditQueue(null)}
          onSave={handleUpdateQueue}
        />
      )}

      {membersQueue && (
        <MembersModal
          queue={membersQueue}
          onClose={() => setMembersQueue(null)}
          onSave={handleUpdateMembers}
          effectiveCid={effectiveCid}
        />
      )}

      {addRaOpen && (
        <AddResourceAccountModal
          onClose={() => setAddRaOpen(false)}
          onSave={handleCreateRa}
          customerId={effectiveCid}
          customers={customers.filter(c => !c.isAdmin)}
          isAdmin={isAdmin}
          callQueues={queues}
        />
      )}

      {editRa && (
        <EditResourceAccountModal
          ra={editRa}
          onClose={() => setEditRa(null)}
          onSave={handleUpdateRa}
          callQueues={queues}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Call Queues</h1>
          <p className="page-subtitle">Manage Teams call queues and resource accounts</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={14} className={syncing ? 'spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync from Teams'}
          </button>
          <button className="btn btn-primary" onClick={() => setAddQueueOpen(true)}>
            <Plus size={14} /> Add Call Queue
          </button>
          <button className="btn btn-primary" onClick={() => setAddRaOpen(true)} style={{ background: 'var(--purple)' }}>
            <Plus size={14} /> Add Resource Account
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PhoneCall size={16} color="var(--info)" />
            <span className="card-title">Call Queues</span>
            <span style={{ fontSize: 11, color: 'var(--muted-3)', background: 'var(--border)', padding: '1px 8px', borderRadius: 10, marginLeft: 4 }}>
              {queues.length}
            </span>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-3)' }}>Loading…</div>
        ) : queues.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-3)' }}>
            No call queues found. Click "Sync from Teams" to import existing queues.
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <SortTh label="Name"       sortKey="name"            currentKey={qSortKey} currentDir={qSortDir} onSort={qHandleSort} />
                  {isAdmin && !effectiveCid && <SortTh label="Customer" sortKey="customer_name" currentKey={qSortKey} currentDir={qSortDir} onSort={qHandleSort} />}
                  <SortTh label="Routing"    sortKey="routing_method"  currentKey={qSortKey} currentDir={qSortDir} onSort={qHandleSort} />
                  <SortTh label="Agents"     sortKey="agents"          currentKey={qSortKey} currentDir={qSortDir} onSort={qHandleSort} />
                  <th>Overflow</th>
                  <th>Timeout</th>
                  <th>MoH</th>
                  <SortTh label="Last Synced" sortKey="last_synced"   currentKey={qSortKey} currentDir={qSortDir} onSort={qHandleSort} />
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedQueues.map(q => (
                  <tr key={q.id}>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text-2)', fontSize: 13 }}>{q.name}</div>
                      {q.description && <div style={{ fontSize: 11, color: 'var(--muted-3)' }}>{q.description}</div>}
                    </td>
                    {isAdmin && !effectiveCid && <td style={{ fontSize: 12, color: 'var(--muted)' }}>{q.customer_name}</td>}
                    <td>
                      <span style={{
                        fontSize: 11, color: 'var(--purple)',
                        background: '#3b076433', border: '1px solid #6b21a833',
                        padding: '2px 7px', borderRadius: 10, fontFamily: 'monospace',
                      }}>{q.routing_method}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {(() => {
                        const agentCount = (q.agents || []).length;
                        const groupCount = (() => { try { return JSON.parse(q.groups_json || '[]').length; } catch { return 0; } })();
                        if (agentCount === 0 && groupCount === 0) return <span style={{ color: 'var(--muted-3)' }}>—</span>;
                        return (
                          <span>
                            {agentCount > 0 && <span>{agentCount} users</span>}
                            {agentCount > 0 && groupCount > 0 && <span style={{ color: 'var(--muted-3)' }}> · </span>}
                            {groupCount > 0 && <span style={{ color: 'var(--purple)' }}>{groupCount} groups</span>}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                      <div>{q.overflow_threshold} calls</div>
                      <div style={{ color: 'var(--muted-3)' }}>{q.overflow_action}</div>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                      <div>{q.timeout_threshold}s</div>
                      <div style={{ color: 'var(--muted-3)' }}>{q.timeout_action}</div>
                    </td>
                    <td>
                      {q.music_on_hold
                        ? <span className="badge badge-green" style={{ fontSize: 10 }}>On</span>
                        : <span style={{ color: 'var(--muted-3)', fontSize: 11 }}>Off</span>}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--muted-3)' }}>{fmtDate(q.last_synced)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-xs" onClick={() => setMembersQueue(q)} title="Configure members">
                          <Users size={11} />
                        </button>
                        <button className="btn btn-secondary btn-xs" onClick={() => setEditQueue(q)} title="Edit">
                          <Edit2 size={11} />
                        </button>
                        <button className="btn btn-secondary btn-xs" onClick={() => handleDeleteQueue(q)} title="Delete" style={{ color: 'var(--err)' }}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Key size={16} color="var(--purple)" />
            <span className="card-title">Resource Accounts</span>
            <span style={{ fontSize: 11, color: 'var(--muted-3)', background: 'var(--border)', padding: '1px 8px', borderRadius: 10, marginLeft: 4 }}>
              {accounts.length}
            </span>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-3)' }}>Loading…</div>
        ) : accounts.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-3)' }}>
            No resource accounts found. Sync from Teams or create one manually.
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <SortTh label="Display Name" sortKey="display_name"    currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />
                  {isAdmin && !effectiveCid && <SortTh label="Customer"  sortKey="customer_name" currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />}
                  <SortTh label="UPN"          sortKey="upn"             currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />
                  <SortTh label="Type"         sortKey="application_type" currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />
                  <SortTh label="Phone"        sortKey="phone_number"    currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />
                  <SortTh label="License"      sortKey="license_status"  currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />
                  <SortTh label="Linked Queue" sortKey="queue"           currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />
                  <SortTh label="Last Synced"  sortKey="last_synced"     currentKey={raSortKey} currentDir={raSortDir} onSort={raHandleSort} />
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAccounts.map(ra => (
                  <tr key={ra.id}>
                    <td style={{ fontWeight: 500, color: 'var(--text-2)', fontSize: 13 }}>{ra.display_name}</td>
                    {isAdmin && !effectiveCid && <td style={{ fontSize: 12, color: 'var(--muted)' }}>{ra.customer_name}</td>}
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--muted)' }}>{ra.upn || '—'}</span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: 11,
                        color: ra.application_type === 'CallQueue' ? 'var(--info)' : 'var(--warn)',
                        background: ra.application_type === 'CallQueue' ? 'var(--info-bg)' : '#2d1a0033',
                        border: `1px solid ${ra.application_type === 'CallQueue' ? 'var(--info-border)' : '#92400e44'}`,
                        padding: '2px 7px', borderRadius: 10,
                      }}>
                        {ra.application_type === 'CallQueue' ? 'Call Queue' : 'Auto Attendant'}
                      </span>
                    </td>
                    <td>
                      {ra.phone_number
                        ? <span style={{ fontFamily: 'monospace', color: 'var(--info)', fontSize: 12 }}>{ra.phone_number}</span>
                        : <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>}
                    </td>
                    <td><LicenseBadge status={ra.license_status} /></td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{ra.queue_name || <span style={{ color: 'var(--muted-3)' }}>—</span>}</td>
                    <td style={{ fontSize: 11, color: 'var(--muted-3)' }}>{fmtDate(ra.last_synced)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-xs" onClick={() => setEditRa(ra)} title="Edit">
                          <Edit2 size={11} />
                        </button>
                        {ra.ms_account_id && (
                          <button
                            className="btn btn-secondary btn-xs"
                            disabled={licensingRaId === ra.id}
                            onClick={() => handleLicense(ra, ra.license_status === 'licensed' ? 'remove' : 'assign')}
                            title={ra.license_status === 'licensed' ? 'Remove license' : 'Assign license'}
                            style={{ color: ra.license_status === 'licensed' ? 'var(--err)' : 'var(--ok)' }}
                          >
                            <Key size={11} />
                          </button>
                        )}
                        <button className="btn btn-secondary btn-xs" onClick={() => handleDeleteRa(ra)} title="Delete" style={{ color: 'var(--err)' }}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
